import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { APP_LABELS, isAppKey, type AppKey } from '@qms360/shared';
import { fetchAppHome } from '../lib/api.js';
import { AppTopBar } from '../components/AppTopBar.js';
import { t } from '../lib/i18n.js';

/**
 * An application's home screen (ADR-0001, spec §14).
 *
 * Each of the three apps has its own. This component is shared because the design
 * system is shared; the apps stay distinct through their identity mark, their
 * modules and their own Settings.
 *
 * ADR-0005 / spec §14: modules are HIDDEN, not disabled. And the empty state below
 * is the specified behaviour for a user with a Platform Role but no Workspace Role
 * in this app — not an error, and not something to hide behind a redirect.
 */

/**
 * Where a module tile goes, for the modules that exist.
 *
 * Keyed by APP first, because module keys repeat across the three apps —
 * `approvals` exists in all of them and means a different screen in each. A flat
 * map would send App 2's approver to App 1's inbox.
 *
 * A tile with no route is a shell and renders as plain text — better than a link
 * that goes nowhere, which teaches people the navigation is unreliable.
 */
const MODULE_ROUTE: Record<AppKey, Record<string, string>> = {
  qaqc: {
    metrics: '/qaqc/periods',
    'material-inspection': '/qaqc/periods',
    qtbt: '/qaqc/periods',
    'quality-brief': '/qaqc/periods',
    approvals: '/qaqc/approvals',
    dashboard: '/qaqc/dashboard',
    'document-governance': '/qaqc/document-governance',
    'customer-satisfaction': '/qaqc/periods',
    reports: '/qaqc/reports',
    delegations: '/qaqc/delegations',
  },
  'lesson-learned': {
    'new-lesson': '/lesson-learned/new',
    'my-lessons': '/lesson-learned/log',
    log: '/lesson-learned/log',
    approvals: '/lesson-learned/approvals',
    escalations: '/lesson-learned/reports?report=lesson.escalations',
    reports: '/lesson-learned/reports',
    delegations: '/lesson-learned/delegations',
  },
  audit: {
    schedule: '/audit/schedules',
    'schedule-approvals': '/audit/schedules',
    checklists: '/audit/checklists',
    plans: '/audit/plans',
    execution: '/audit/audits',
    invited: '/audit/invited',
    findings: '/audit/findings',
    cars: '/audit/findings',
    dashboard: '/audit/dashboard',
    reports: '/audit/reports',
    delegations: '/audit/delegations',
  },
};

const MODULE_BLURB: Record<string, string> = {
  metrics: 'NCR, RFI and RMI counts with ageing buckets and auto-calculated closure rates.',
  'material-inspection': 'MIRN counts, OSD and status distribution, reconciled to the total.',
  qtbt: 'Toolbox talk count, attendance and duration.',
  'customer-satisfaction': 'Six rated questions and free-text feedback, averaged per month.',
  'document-governance': 'Daily submittals and drawings, their status and review time.',
  'quality-brief': 'Monthly narrative, with an AI first draft you accept, edit or reject.',
  approvals: 'Items waiting on your approval.',
  dashboard: 'Filterable by project group, project, month and category.',
  'new-lesson': 'Guided, dropdown-first capture with photo evidence. Works offline.',
  'my-lessons': 'The Lesson Learned Log — filter it to the lessons you raised.',
  log: 'Searchable, filterable knowledge base of every approved lesson.',
  escalations: 'Open SLA clocks and where they have escalated to.',
  reports: 'Every report for this application, on screen and as Excel, CSV or PDF.',
  delegations: 'Hand your roles to a colleague while you are away, and see what you hold for others.',
  schedule: 'Build the annual audit programme and submit it for approval.',
  'schedule-approvals': 'Approve or send back the annual audit programme.',
  checklists: 'Write the checklists auditors work through, question by question.',
  plans: 'Scope, criteria, date and location, shared with process owners.',
  execution: 'Opening and closing minutes, evidence capture, findings.',
  findings: 'Classify findings as conformity, observation, minor or major NC.',
  cars: 'Corrective actions — one per responsible department, tracked independently.',
  invited: 'Read the audits you are invited to, until your invitation ends or the audit closes.',
};

export function AppHome() {
  const params = useParams<{ app: string }>();
  const app = params.app;

  const { data, isLoading, error } = useQuery({
    queryKey: ['app-home', app],
    queryFn: () => fetchAppHome(app!),
    enabled: Boolean(app) && isAppKey(app),
  });

  if (!app || !isAppKey(app)) {
    return <PageMessage title={t("Unknown application")} body={t("That application does not exist.")} />;
  }

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey} />

      <main className="mx-auto max-w-7xl px-4 py-8">
        <h1 className="m-0 text-2xl font-semibold text-content-primary">{t(APP_LABELS[app])}</h1>

        {isLoading && <p className="text-content-secondary">{t("Loading…")}</p>}
        {error && <p className="text-tone-critical">{t("Could not load this application.")}</p>}

        {data && !data.hasWorkspaceAccess && (
          <section
            className="mt-6 rounded-md border border-border bg-tone-info-surface p-6"
            role="status"
          >
            <h2 className="m-0 text-base font-semibold text-content-primary">
              {t("No modules available yet")}
            </h2>
            <p className="mb-0 mt-2 text-content-secondary">{t(data.emptyStateReason)}</p>
          </section>
        )}

        {data && data.hasWorkspaceAccess && (
          <>
            <p className="mt-2 text-content-secondary">
              {app === 'qaqc'
                ? t("Open a monthly reporting period to enter figures, review the calculated index, and submit for approval.")
                : app === 'lesson-learned'
                  ? t("Capture a lesson on site, with or without a signal. Anything recorded offline syncs when you reconnect.")
                  : t("Build the annual programme, plan and run an audit, classify what you find, and track each corrective action through to verified closure.")}
            </p>

            <ul className="mt-6 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
              {data.modules.map((module) => (
                <li
                  key={module.key}
                  className="rounded-md border border-border bg-surface p-5 shadow-card"
                >
                  <h2 className="m-0 text-base font-semibold text-content-primary">
                    {MODULE_ROUTE[app][module.key] ? (
                      <Link
                        to={MODULE_ROUTE[app][module.key]!}
                        className="text-ag-purple no-underline hover:underline"
                      >
                        {t(module.label)}
                      </Link>
                    ) : (
                      t(module.label)
                    )}
                  </h2>
                  <p className="mb-0 mt-2 text-sm text-content-secondary">
                    {t(MODULE_BLURB[module.key] ?? '')}
                  </p>
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    </div>
  );
}

export function PageMessage({ title, body }: { title: string; body: string }) {
  return (
    <main className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="m-0 text-xl font-semibold">{t(title)}</h1>
      <p className="mt-2 text-content-secondary">{t(body)}</p>
    </main>
  );
}
